// ============================================================================
// SCHEDULER HOME VIEW — Fires + Engine Dashboard (Scheduler-specific)
//
// Shows ONLY Ready to Schedule and Urgent Waiting cards.
// Same data source and layout pattern as OpsHomeView.
//
// SLA clocks:
//   - Scheduling clock starts at triageCompletedAt (set when triage approves)
//   - Urgent clock starts at statusEnteredAt (age in NEW status)
//   - Thresholds loaded from tenant Impact Settings, falls back to defaults
//
// Zero-PHI invariant: this view never reads from /phi/**.
// ============================================================================

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  listOpsActiveItems, WORKFLOW_STATUS, WORKFLOW_PRIORITY,
  getImpactSettings, DEFAULT_IMPACT_SETTINGS, queueSlaStatus,
} from '../../core/index.js';
import {
  Activity, RefreshCw, AlertTriangle, Flame, Inbox,
  Clock, AlertCircle, ArrowRight, Zap,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function toMillis(ts) {
  if (!ts) return null;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

function businessHoursToMs(hours) {
  return hours * 60 * 60 * 1000;
}

function isStuck(item, nowMs, slaConfig) {
  const status = item.workflow?.status;
  const priority = item.workflow?.priority;

  const scheduledSlaHours = slaConfig?.states?.SCHEDULED?.slaHours ?? 168;

  if (status === WORKFLOW_STATUS.SCHEDULED) {
    const apptStr = item.workflow?.scheduling?.appointmentDate;
    // Anchor on the actual scheduled time; fall back to status entry only if absent.
    const scheduledMs = toMillis(item.workflow?.scheduling?.scheduledAt)
                     || toMillis(item.workflow?.statusEnteredAt);
    if (!apptStr || !scheduledMs) return false;
    // Parse the calendar date deterministically (UTC), independent of browser timezone.
    const apptMs = new Date(apptStr + 'T00:00:00Z').getTime();
    if (Number.isNaN(apptMs)) return false;
    return (apptMs - scheduledMs) >= businessHoursToMs(scheduledSlaHours);
  }

  if (status === WORKFLOW_STATUS.READY_TO_SCHEDULE) {
    const triageMs = toMillis(item.workflow?.triageCompletedAt)
                  || toMillis(item.workflow?.triage?.triageCompletedAt);
    if (!triageMs) return false;
    const schedulingSlaHours = slaConfig?.scheduling?.businessHours ?? 48;
    return (nowMs - triageMs) >= businessHoursToMs(schedulingSlaHours);
  }

  if (status === WORKFLOW_STATUS.NEW && (priority === WORKFLOW_PRIORITY.HIGH || priority === WORKFLOW_PRIORITY.URGENT)) {
    const enteredMs = toMillis(item.workflow?.statusEnteredAt)
                   || toMillis(item.workflow?.statusUpdatedAt)
                   || toMillis(item.workflow?.createdAt);
    if (!enteredMs) return false;
    const urgentSlaHours = slaConfig?.triage?.urgentBusinessHours ?? 4;
    return (nowMs - enteredMs) >= businessHoursToMs(urgentSlaHours);
  }

  return false;
}

function isUrgent(item) {
  return item.workflow?.priority === WORKFLOW_PRIORITY.HIGH ||
         item.workflow?.priority === WORKFLOW_PRIORITY.URGENT ||
         item.workflow?.sourceOutcomeStatus === 'urgent';
}

function isSchedulable(item) {
  if (item.workflow?.onHold === true) return false;
  return !!(item.workflow?.triage?.triageCompletedAt || item.workflow?.triageCompletedAt);
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-slate-800/30 border border-slate-700/20 rounded-2xl p-5 animate-pulse">
      <div className="h-3 w-24 bg-slate-700/60 rounded mb-3" />
      <div className="h-10 w-16 bg-slate-700/60 rounded" />
    </div>
  );
}

// Severity tiers. Color is driven by queueSlaStatus (share-of-queue warn/critical),
// NOT by raw count — but a count > 0 below warn still renders an "active" (visible)
// tier so the work requiring action is never hidden.
const FIRE_SEVERITY = {
  critical: { box: 'bg-red-500/10 text-red-400 border-red-500/25 shadow-lg',     pulse: 'bg-red-500' },
  warn:     { box: 'bg-amber-500/10 text-amber-400 border-amber-500/25 shadow-lg', pulse: 'bg-amber-500' },
  active:   { box: 'bg-slate-800/50 text-slate-200 border-slate-700/40',         pulse: '' },
  empty:    { box: 'bg-slate-800/30 text-slate-500 border-slate-700/30',         pulse: '' },
};

function FireCard({ label, count, status = 'ok', sla, icon: Icon, onClick, learningTarget, isHighlighted }) {
  // count > 0 always visible: critical/warn escalate by threshold; otherwise "active".
  const tier = count > 0
    ? (status === 'critical' ? 'critical' : status === 'warn' ? 'warn' : 'active')
    : 'empty';
  const sev = FIRE_SEVERITY[tier];
  const isAlert = tier === 'critical' || tier === 'warn';
  const hasWork = count > 0;
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-learning-target={learningTarget}
      className={`relative overflow-hidden rounded-2xl border p-5 transition-all text-left w-full
      ${sev.box} ${hasWork && onClick ? 'cursor-pointer hover:brightness-110' : ''}${isHighlighted ? ' learning-focus-highlight-subtle' : ''}`}
    >
      {isAlert && sev.pulse && (
        <div className={`absolute inset-0 ${sev.pulse} opacity-10 rounded-2xl`} />
      )}
      <div className="relative flex items-start justify-between">
        <div>
          <p className={`text-xs font-medium mb-1 ${hasWork ? 'opacity-80' : 'text-slate-500'}`}>
            {label}
          </p>
          <p className={`text-4xl font-black tracking-tight ${hasWork ? '' : 'text-slate-600'}`}>
            {count}
          </p>
          <p className={`text-xs mt-1 ${hasWork ? 'opacity-60' : 'text-slate-600'}`}>
            {sla}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Icon className={`w-6 h-6 ${hasWork ? 'opacity-40' : 'opacity-20'}`} />
          {hasWork && onClick && (
            <ArrowRight className="w-4 h-4 opacity-40" />
          )}
        </div>
      </div>
    </Tag>
  );
}

function EngineCard({ label, count, icon: Icon, colorTw, subtitle, learningTarget, isHighlighted }) {
  return (
    <div data-learning-target={learningTarget} className={`bg-slate-800/50 border border-slate-700/40 rounded-2xl p-5 flex flex-col gap-1${isHighlighted ? ' learning-focus-highlight-subtle' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${colorTw}`} />
        <p className="text-xs font-medium text-slate-400">{label}</p>
      </div>
      <p className={`text-3xl font-bold ${colorTw}`}>
        {count}
      </p>
      {subtitle && (
        <p className="text-xs text-slate-500">{subtitle}</p>
      )}
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function SchedulerHomeView({ onNavigate, activeFocusTarget }) {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId;

  const [items, setItems]       = useState(null);
  const [error, setError]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [slaConfig, setSlaConfig] = useState(DEFAULT_IMPACT_SETTINGS.sla);

  const load = async () => {
    if (!tenantId) return;
    try {
      const [data, settings] = await Promise.all([
        listOpsActiveItems(tenantId),
        getImpactSettings(tenantId),
      ]);
      setItems(data);
      setSlaConfig(settings?.sla ?? DEFAULT_IMPACT_SETTINGS.sla);
      setError(null);
    } catch (err) {
      console.error('[SchedulerHomeView] Failed to load ops items:', err);
      setError('Failed to load operational data. Please retry.');
    }
  };

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    setItems(null);
    await load();
    setRefreshing(false);
  };

  const loading = items === null && !error;

  const schedulingSlaHours = slaConfig?.scheduling?.businessHours ?? 48;
  const urgentSlaHours = slaConfig?.triage?.urgentBusinessHours ?? 4;

  // Tenant warn/critical thresholds (share-of-queue) — same model as Ops/Triage.
  const warnPct = slaConfig?.thresholds?.warnPercent ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds.warnPercent;
  const critPct = slaConfig?.thresholds?.criticalPercent ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds.criticalPercent;

  // ── Derive dashboard numbers (scheduler-relevant only) ─────────────────

  const dashboard = useMemo(() => {
    if (!items) return null;
    const nowMs = Date.now();

    const byStatus = {
      [WORKFLOW_STATUS.NEW]:               [],
      [WORKFLOW_STATUS.READY_TO_SCHEDULE]: [],
    };

    for (const item of items) {
      const status = item.workflow?.status;
      if (status && byStatus[status]) {
        byStatus[status].push(item);
      }
    }

    const schedulableReady = byStatus[WORKFLOW_STATUS.READY_TO_SCHEDULE].filter(isSchedulable);
    const urgentReady = schedulableReady.filter(isUrgent);
    const routineReady = schedulableReady.filter((it) => !isUrgent(it));

    const stuckRoutine = routineReady.filter(
      (it) => isStuck(it, nowMs, slaConfig)
    );
    const stuckUrgent = urgentReady.filter((it) => isStuck(it, nowMs, slaConfig));

    return {
      stuckReady:      stuckRoutine.length,
      stuckUrgent:     stuckUrgent.length,
      totalFires:      stuckRoutine.length + stuckUrgent.length,

      readyToSchedule: routineReady.length,
      urgentQueue:     urgentReady.length,

      totalActive:     items.length,
    };
  }, [items, slaConfig]);

  // Severity from share-of-queue (counts preserved; color follows thresholds).
  const readyStatus  = dashboard ? queueSlaStatus(dashboard.stuckReady,  dashboard.readyToSchedule, warnPct, critPct) : 'ok';
  const urgentStatus = dashboard ? queueSlaStatus(dashboard.stuckUrgent, dashboard.urgentQueue,     warnPct, critPct) : 'ok';

  // ── Render ──────────────────────────────────────────────────────────────

  const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div data-learning-target="queue-health-header" className={`flex items-center justify-between${activeFocusTarget === 'queue-health-header' ? ' learning-focus-highlight-subtle rounded-xl p-2 -m-2' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Queue Health</h1>
            <p className="text-xs text-slate-400">
              Live — {todayStr}
              {dashboard && (
                <span className="ml-2 text-slate-500">
                  · {dashboard.totalActive} active referral{dashboard.totalActive !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                     text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={handleRefresh}
            className="text-xs underline underline-offset-2 hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── ROW 1: THE FIRES ───────────────────────────────────────────────── */}
      <section data-learning-target="fires-section" className={activeFocusTarget === 'fires-section' ? 'learning-focus-highlight-subtle rounded-xl p-2 -m-2' : undefined}>
        <div className="flex items-center gap-2 mb-4">
          <Flame className="w-4 h-4 text-red-400" />
          <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider">
            Fires — Stuck Items
          </h2>
          {dashboard && dashboard.totalFires > 0 && (
            <span className="ml-1 w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-2 gap-4">
            <FireCard
              label="Ready to Schedule"
              count={dashboard.stuckReady}
              status={readyStatus}
              sla={`Breaching ${schedulingSlaHours}h scheduling SLA`}
              icon={ArrowRight}
              onClick={dashboard.stuckReady > 0 && onNavigate ? () => onNavigate('scheduling') : undefined}
              learningTarget="ready-to-schedule-fire-card"
              isHighlighted={activeFocusTarget === 'ready-to-schedule-fire-card'}
            />
            <FireCard
              label="Urgent Waiting"
              count={dashboard.stuckUrgent}
              status={urgentStatus}
              sla={`Breaching ${urgentSlaHours}h urgent SLA`}
              icon={Zap}
              onClick={dashboard.stuckUrgent > 0 && onNavigate ? () => onNavigate('scheduling') : undefined}
              learningTarget="urgent-waiting-fire-card"
              isHighlighted={activeFocusTarget === 'urgent-waiting-fire-card'}
            />
          </div>
        )}

        {dashboard && dashboard.totalFires === 0 && (
          <div className="mt-2 px-4 py-3 bg-emerald-500/5 border border-emerald-500/15
                          rounded-xl text-emerald-400 text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            All items within SLA — no fires right now.
          </div>
        )}
      </section>

      {/* ── ROW 2: THE ENGINE ──────────────────────────────────────────────── */}
      <section data-learning-target="engine-section" className={activeFocusTarget === 'engine-section' ? 'learning-focus-highlight-subtle rounded-xl p-2 -m-2' : undefined}>
        <div className="flex items-center gap-2 mb-4">
          <Inbox className="w-4 h-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider">
            Engine — Total Volume
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-2 gap-4">
            <EngineCard
              label="Ready to Schedule"
              count={dashboard.readyToSchedule}
              icon={ArrowRight}
              colorTw="text-indigo-400"
              subtitle="Triaged — needs appointment"
              learningTarget="ready-to-schedule-volume-card"
              isHighlighted={activeFocusTarget === 'ready-to-schedule-volume-card'}
            />
            <EngineCard
              label="Urgent Queue"
              count={dashboard.urgentQueue}
              icon={Zap}
              colorTw="text-rose-400"
              subtitle="Red-flag referrals"
              learningTarget="urgent-queue-volume-card"
              isHighlighted={activeFocusTarget === 'urgent-queue-volume-card'}
            />
          </div>
        )}
      </section>

      {/* ── Footer note ────────────────────────────────────────────────────── */}
      <p data-learning-target="queue-health-footer-or-refresh" className={`text-xs text-slate-600 text-center${activeFocusTarget === 'queue-health-footer-or-refresh' ? ' learning-focus-highlight-subtle rounded-lg p-2' : ''}`}>
        Live data from workflow states · Scheduling clock starts when triage is completed · Business-hours SLA
      </p>
    </div>
  );
}
